import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiSuccess, AttachmentWithDownloadUrl } from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

import { publicConsultantSiteKeys } from './useConsultantSite';

export function useProfileAvatar(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.profile.avatar(),
    queryFn: async (): Promise<AttachmentWithDownloadUrl | null> => {
      const response =
        await apiClient.get<ApiSuccess<AttachmentWithDownloadUrl | null>>('/profile/avatar');
      return response.data.data;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useUploadProfileAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<AttachmentWithDownloadUrl> => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post<ApiSuccess<AttachmentWithDownloadUrl>>(
        '/profile/avatar',
        formData,
        { headers: { 'Content-Type': undefined } },
      );
      return response.data.data;
    },
    onSuccess: (avatar) => {
      queryClient.setQueryData(queryKeys.profile.avatar(), avatar);
      void queryClient.invalidateQueries({ queryKey: queryKeys.consultantSite.me() });
      void queryClient.invalidateQueries({ queryKey: publicConsultantSiteKeys.all });
    },
  });
}

export function useDeleteProfileAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await apiClient.delete('/profile/avatar');
    },
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.profile.avatar(), null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.consultantSite.me() });
      void queryClient.invalidateQueries({ queryKey: publicConsultantSiteKeys.all });
    },
  });
}
